import { useCallback, useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { employeeOnboardingService } from "../../services/api";
import { applyOnboardingNotifications } from "../../utils/onboardingNotifications";
import { getAuthJson } from "../../utils/authStorage";
import EmployeeCodePromptDialog from "./EmployeeCodePromptDialog";
import toast from "react-hot-toast";

function formatDt(value) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function buildOrgFields(orgDisplay = {}, candidateId = "") {
  const rows = [
    ["Candidate ID", candidateId || orgDisplay.candidateId],
    ["Name", orgDisplay.name],
    ["Official Email", orgDisplay.officialEmail],
    ["Personal Email", orgDisplay.personalEmail],
    ["Mobile", orgDisplay.mobile],
    ["Branch", orgDisplay.branch],
    ["Department", orgDisplay.department],
    ["Sub Department", orgDisplay.subDepartment],
    ["Designation", orgDisplay.designation],
    ["Grade", orgDisplay.grade],
    ["Category", orgDisplay.category],
    ["Date of Join", orgDisplay.dateOfJoin],
    ["Employment Type", orgDisplay.employmentType],
    ["Employment Status", orgDisplay.employmentStatus],
    ["Reporting Manager", orgDisplay.reportingManager],
  ];
  return rows.filter(([, v]) => v != null && String(v).trim() !== "" && String(v).trim() !== "-");
}

function buildChecklistActivity(task, row) {
  const activity = [];
  const assignedTo = row.assigneeDisplay || row.pendingWith || task?.actionRole || null;
  const triggeredAt = task?.triggeredAt || row.submittedAt || null;
  if (assignedTo || triggeredAt) {
    activity.push({
      key: "assigned",
      action: "Assigned",
      actor: assignedTo || "—",
      at: triggeredAt,
      remarks: null,
    });
  }
  if (
    task?.status === "rejected" ||
    task?.status === "sent_back" ||
    row.checklistTaskStatus === "rejected" ||
    row.checklistTaskStatus === "sent_back"
  ) {
    activity.push({
      key: "rejected",
      action: "Rejected",
      actor:
        task?.rejectedByName ||
        task?.sentBackByName ||
        row.rejectedByName ||
        row.approvedBy ||
        "Approver",
      at: task?.rejectedAt || task?.sentBackAt || row.rejectedAt || row.when || null,
      remarks: task?.rejectedComments || task?.sentBackComments || row.rejectedComments || null,
    });
  }
  if (task?.status === "done" || row.checklistTaskStatus === "done") {
    activity.push({
      key: "done",
      action: "Done",
      actor: task?.completedByName || row.completedByName || row.approvedBy || "Approver",
      at: task?.completedAt || row.completedAt || row.when || null,
      remarks: task?.completedComments || row.completedComments || null,
    });
  }
  return activity;
}

export default function OnboardingInsightDetailModal({
  darkMode = false,
  row,
  onClose,
  onUpdated,
}) {
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [detail, setDetail] = useState(null);
  const [comments, setComments] = useState("");
  const [showEmpCodeDialog, setShowEmpCodeDialog] = useState(false);
  const [pendingCreateAction, setPendingCreateAction] = useState(null);

  const currentUser = getAuthJson("currentUser");
  const isAdmin = ["admin", "super_admin"].includes(currentUser?.role);

  const loadDetail = useCallback(async () => {
    if (!row?.onboardingRequestId) return;
    setLoading(true);
    try {
      const res = await employeeOnboardingService.getRequest(row.onboardingRequestId);
      setDetail(res?.data || null);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load onboarding detail");
    } finally {
      setLoading(false);
    }
  }, [row?.onboardingRequestId]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const orgDisplay = detail?.orgDisplay || {};
  const orgFields = buildOrgFields(orgDisplay, detail?.candidateId || row?.candidateId);
  const completionMeta = detail?.payload?.completionMeta || {};
  const checklistTasks = Array.isArray(detail?.payload?.checklistTasks)
    ? detail.payload.checklistTasks
    : [];
  const approvals = Array.isArray(detail?.approvals) ? detail.approvals : [];
  const rawStatus = detail?.status || row?.rawStatus || "draft";
  const isChecklistRow = row?.rowType === "checklist";
  const requiresManualEmployeeCode = detail ? !!detail.requiresManualEmployeeCode : false;
  const nextEmployeeCodePreview = detail?.nextEmployeeCodePreview || null;
  const isCompleted = rawStatus === "completed";

  const liveChecklistTask = isChecklistRow
    ? checklistTasks.find((t) => t.id === row.checklistTaskId) || null
    : null;
  const checklistTaskStatus =
    liveChecklistTask?.status || row?.checklistTaskStatus || "pending";
  const checklistIsDone = checklistTaskStatus === "done";
  const checklistIsRejected =
    checklistTaskStatus === "rejected" || checklistTaskStatus === "sent_back";
  const checklistIsPending = !checklistIsDone && !checklistIsRejected;
  const profileComplete =
    detail?.profileComplete === true || row?.profileComplete === true;
  const profileCompletionPercent =
    detail?.profileCompletionPercent ?? row?.profileCompletionPercent ?? null;
  const canActOnChecklist =
    checklistIsPending && (row?.canRejectChecklist !== false || profileComplete);
  const canMarkDone =
    checklistIsPending && profileComplete && row?.canMarkDoneChecklist !== false;
  const canReject = checklistIsPending && row?.canRejectChecklist !== false;
  const canApproveRequest =
    rawStatus === "pending_approval" &&
    isAdmin &&
    (detail?.profileComplete === true || row?.canApprove === true);

  const candidateLabel =
    row?.candidateName ||
    orgDisplay.name ||
    row?.candidateId ||
    row?.employeeCode ||
    "Candidate";
  const taskTitle = liveChecklistTask?.name || row?.employeeName || "Onboarding task";
  const checklistStatusLabel = checklistIsDone
    ? "Done"
    : checklistIsRejected
      ? "Rejected"
      : profileComplete
        ? "Pending"
        : "Awaiting profile completion";

  const activity = useMemo(
    () => (isChecklistRow && row ? buildChecklistActivity(liveChecklistTask, row) : []),
    [isChecklistRow, liveChecklistTask, row]
  );

  if (!row) return null;

  const refreshParent = () => {
    if (typeof onUpdated === "function") onUpdated();
    loadDetail();
  };

  const closeEmpCodeDialog = () => {
    setShowEmpCodeDialog(false);
    setPendingCreateAction(null);
  };

  const handleSubmitCreate = async (empCode) => {
    if (!row.onboardingRequestId) return;
    setActing(true);
    try {
      const payload = empCode ? { empCode: empCode.trim() } : {};
      const res = await employeeOnboardingService.submitRequest(row.onboardingRequestId, payload);
      applyOnboardingNotifications(res?.notifications);
      if (res?.autoApproved) {
        const createdCode = res?.employee?.empCode || res?.employee?.emp_code || empCode;
        toast.success(
          createdCode
            ? `Employee created with code ${createdCode}`
            : "Employee created successfully"
        );
      } else {
        toast.success("Submitted for approval");
      }
      closeEmpCodeDialog();
      refreshParent();
      onClose();
    } catch (error) {
      const msg = error.response?.data?.message || "Failed to submit onboarding";
      if (error.response?.data?.requiresManualEmpCode) {
        setPendingCreateAction("submit");
        setShowEmpCodeDialog(true);
      }
      toast.error(msg);
    } finally {
      setActing(false);
    }
  };

  const handleApprove = async (empCode) => {
    if (!row.onboardingRequestId) return;
    const reason = comments.trim();
    if (!reason) {
      toast.error("Remarks are required");
      return;
    }
    setActing(true);
    try {
      const payload = empCode ? { empCode: empCode.trim() } : {};
      const res = await employeeOnboardingService.approveRequest(
        row.onboardingRequestId,
        reason,
        payload
      );
      applyOnboardingNotifications(res?.notifications);
      const createdCode = res?.employee?.empCode || res?.employee?.emp_code || empCode;
      toast.success(
        createdCode ? `Onboarding approved — employee code ${createdCode}` : "Onboarding approved"
      );
      closeEmpCodeDialog();
      refreshParent();
      onClose();
    } catch (error) {
      const msg = error.response?.data?.message || "Approval failed";
      if (error.response?.data?.requiresManualEmpCode) {
        setPendingCreateAction("approve");
        setShowEmpCodeDialog(true);
      }
      toast.error(msg);
    } finally {
      setActing(false);
    }
  };

  const handleReject = async () => {
    if (!row.onboardingRequestId) return;
    const reason = comments.trim();
    if (!reason) {
      toast.error("Remarks are required");
      return;
    }
    setActing(true);
    try {
      const res = await employeeOnboardingService.rejectRequest(row.onboardingRequestId, reason);
      applyOnboardingNotifications(res?.notifications);
      toast.success("Onboarding rejected");
      refreshParent();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || "Reject failed");
    } finally {
      setActing(false);
    }
  };

  const handleCompleteChecklist = async () => {
    if (!row.onboardingRequestId || !row.checklistTaskId) return;
    if (!comments.trim()) {
      toast.error("Remarks are required");
      return;
    }
    setActing(true);
    try {
      await employeeOnboardingService.completeChecklistTask(
        row.onboardingRequestId,
        row.checklistTaskId,
        comments.trim()
      );
      toast.success("Checklist task marked Done");
      refreshParent();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to mark task Done");
    } finally {
      setActing(false);
    }
  };

  const handleRejectChecklist = async () => {
    if (!row.onboardingRequestId || !row.checklistTaskId) return;
    if (!comments.trim()) {
      toast.error("Remarks are required to reject");
      return;
    }
    setActing(true);
    try {
      const result = await employeeOnboardingService.rejectChecklistTask(
        row.onboardingRequestId,
        row.checklistTaskId,
        comments.trim()
      );
      if (result?.emailSent) {
        toast.success("Task rejected — candidate emailed to complete profile");
      } else {
        toast.success("Task rejected");
        if (result?.emailError) toast.error(`Email not sent: ${result.emailError}`);
      }
      refreshParent();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to reject task");
    } finally {
      setActing(false);
    }
  };

  const promptForEmpCodeIfNeeded = (action) => {
    if (action === "approve" && !comments.trim()) {
      toast.error("Remarks are required");
      return;
    }
    if (isCompleted) {
      toast.success("This onboarding request is already completed");
      return;
    }
    if (requiresManualEmployeeCode) {
      setPendingCreateAction(action);
      setShowEmpCodeDialog(true);
      return;
    }
    if (action === "submit") handleSubmitCreate();
    else handleApprove();
  };

  const confirmEmpCodeAndCreate = (code) => {
    if (!code) {
      toast.error("Employee code is required");
      return;
    }
    if (pendingCreateAction === "approve") {
      handleApprove(code);
    } else {
      handleSubmitCreate(code);
    }
  };

  const inputClass = darkMode
    ? "mt-1 w-full rounded-md border border-[#3C3C4C] bg-[#1D1D1D] px-2 py-1.5 text-xs text-gray-100"
    : "mt-1 w-full rounded-md border border-gray-300 bg-gray-50 px-2 py-1.5 text-xs text-gray-900";

  return (
    <>
      <div className="fixed inset-0 z-[10030] flex items-center justify-center bg-black/50 p-4">
        <div
          className={`flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border shadow-xl ${
            darkMode ? "border-[#3C3C4C] bg-[#121212]" : "border-gray-200 bg-white"
          }`}
        >
          <div
            className={`flex items-center justify-between border-b px-4 py-3 ${
              darkMode ? "border-[#3C3C4C]" : "border-gray-200"
            }`}
          >
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {isChecklistRow ? taskTitle : "Onboarding request"}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {isChecklistRow
                  ? `${candidateLabel}${row.candidateId ? ` · ${row.candidateId}` : ""} · ${checklistStatusLabel}`
                  : `${row.candidateId || row.employeeCode} · ${row.status}`}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded p-1 hover:bg-gray-100 dark:hover:bg-white/10"
            >
              <X size={16} />
            </button>
          </div>

          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 text-xs">
            {loading ? (
              <p className="text-gray-500">Loading…</p>
            ) : (
              <>
                {!isChecklistRow && (
                  <>
                    <div
                      className={`rounded-md border p-3 ${
                        darkMode ? "border-[#3C3C4C] bg-[#1a1a1a]" : "border-gray-200 bg-gray-50"
                      }`}
                    >
                      <div className="mb-2 font-semibold text-gray-800 dark:text-gray-200">
                        Organization details
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {orgFields.map(([label, value]) => (
                          <div key={label}>
                            <div className="text-[10px] uppercase text-gray-500">{label}</div>
                            <div className="font-medium text-gray-900 dark:text-gray-100">
                              {String(value)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {nextEmployeeCodePreview && rawStatus === "draft" && !requiresManualEmployeeCode && (
                      <div
                        className={`rounded-md border p-3 ${
                          darkMode
                            ? "border-sky-900/40 bg-sky-950/20"
                            : "border-sky-200 bg-sky-50"
                        }`}
                      >
                        <div className="font-semibold text-sky-900 dark:text-sky-200">
                          Employee code (automated)
                        </div>
                        <p className="mt-1 text-[11px] text-sky-900/90 dark:text-sky-100/90">
                          The next code from your active structure will be assigned automatically
                          (e.g. <strong>{nextEmployeeCodePreview}</strong>) when you submit.
                        </p>
                      </div>
                    )}

                    {requiresManualEmployeeCode && rawStatus === "draft" && (
                      <div
                        className={`rounded-md border p-3 ${
                          darkMode
                            ? "border-amber-900/40 bg-amber-950/20"
                            : "border-amber-200 bg-amber-50"
                        }`}
                      >
                        <div className="font-semibold text-amber-900 dark:text-amber-200">
                          Employee code required
                        </div>
                        <p className="mt-1 text-[11px] text-amber-900/90 dark:text-amber-100/90">
                          No automated employee code structure is active. You will be asked to enter
                          the employee code when you click Submit & create employee.
                        </p>
                      </div>
                    )}

                    <div>
                      <div className="mb-1 font-semibold text-gray-800 dark:text-gray-200">
                        Approval flow
                      </div>
                      <div className="space-y-1">
                        <div className="flex justify-between gap-2">
                          <span className="text-gray-500">Current status</span>
                          <span className="font-medium">{rawStatus}</span>
                        </div>
                        {completionMeta.approvedByName && (
                          <div className="flex justify-between gap-2">
                            <span className="text-gray-500">Approved by</span>
                            <span className="font-medium">{completionMeta.approvedByName}</span>
                          </div>
                        )}
                        {completionMeta.completedAt && (
                          <div className="flex justify-between gap-2">
                            <span className="text-gray-500">Completed at</span>
                            <span>{formatDt(completionMeta.completedAt)}</span>
                          </div>
                        )}
                        {approvals.map((step) => (
                          <div
                            key={step.id || step.stepOrder}
                            className={`rounded border px-2 py-1 ${
                              darkMode ? "border-[#3C3C4C]" : "border-gray-200"
                            }`}
                          >
                            Step {step.stepOrder}: {step.role} — {step.action}
                            {step.actedAt ? ` · ${formatDt(step.actedAt)}` : ""}
                            {step.comments ? ` · ${step.comments}` : ""}
                          </div>
                        ))}
                        {detail?.workflowSnapshot?.notifyOnlyAutoApproved && (
                          <div className="text-[11px] text-gray-600 dark:text-gray-400">
                            Notify-only workflow: employee will be auto-created; reporting manager
                            will be notified (no approval action required).
                          </div>
                        )}
                      </div>
                    </div>

                    {checklistTasks.length > 0 && (
                      <div>
                        <div className="mb-1 font-semibold text-gray-800 dark:text-gray-200">
                          Checklist tasks
                        </div>
                        <div className="space-y-1">
                          {checklistTasks.map((task) => (
                            <div
                              key={task.id}
                              className={`flex justify-between gap-2 rounded border px-2 py-1 ${
                                darkMode ? "border-[#3C3C4C]" : "border-gray-200"
                              }`}
                            >
                              <span>
                                {task.name}
                                {task.assigneeDisplay ? (
                                  <span className="ml-1 text-[10px] text-gray-500">
                                    · {task.assigneeDisplay}
                                  </span>
                                ) : null}
                              </span>
                              <span className="shrink-0 text-gray-500">
                                {task.status === "done"
                                  ? "Done"
                                  : task.status === "rejected" || task.status === "sent_back"
                                    ? "Rejected"
                                    : task.slaStartsAt && task.dueAt
                                      ? formatDt(task.dueAt)
                                      : "Awaiting profile completion"}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {(rawStatus === "pending_approval" || rawStatus === "draft") && isAdmin && (
                      <>
                        <label className="block text-xs text-gray-700 dark:text-gray-300">
                          Remarks (required for approve / reject)
                        <textarea
                          rows={2}
                          maxLength={250}
                          value={comments}
                          onChange={(e) => setComments(e.target.value)}
                          className={inputClass}
                          placeholder="Enter remarks…"
                        />
                      </label>
                      </>
                    )}
                  </>
                )}

                {isChecklistRow && (
                  <div className="space-y-3">
                    <div
                      className={`rounded-md border px-3 py-2 text-[11px] ${
                        darkMode ? "border-[#3C3C4C] bg-[#1a1a1a]" : "border-gray-200 bg-gray-50"
                      }`}
                    >
                      <div className="text-gray-500 dark:text-gray-400">Assigned to</div>
                      <div className="mt-0.5 font-semibold text-gray-900 dark:text-gray-100">
                        {row.assigneeDisplay || row.pendingWith || "—"}
                      </div>
                      {checklistIsPending && row.pendingAt && row.pendingAt !== "Rejected" ? (
                        <div className="mt-1 text-gray-600 dark:text-gray-400">
                          {profileComplete ? `Due: ${row.pendingAt}` : row.pendingAt}
                        </div>
                      ) : null}
                    </div>

                    <div>
                      <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Activity
                      </div>
                      <div className="space-y-2">
                        {activity.map((step) => (
                          <div
                            key={step.key}
                            className={`rounded-md border px-3 py-2 ${
                              darkMode ? "border-[#3C3C4C]" : "border-gray-200"
                            }`}
                          >
                            <div className="font-semibold text-gray-900 dark:text-gray-100">
                              {step.action}
                              <span className="font-normal text-gray-600 dark:text-gray-300">
                                {" "}
                                by {step.actor}
                              </span>
                            </div>
                            {step.at ? (
                              <div className="mt-0.5 text-[10px] text-gray-500">
                                {formatDt(step.at)}
                              </div>
                            ) : null}
                            {step.remarks ? (
                              <div className="mt-1.5 whitespace-pre-wrap text-[11px] text-gray-700 dark:text-gray-300">
                                Remarks: {step.remarks}
                              </div>
                            ) : null}
                          </div>
                        ))}
                        {activity.length === 0 ? (
                          <div className="text-[11px] text-gray-500">No activity yet.</div>
                        ) : null}
                      </div>
                    </div>

                    {canActOnChecklist ? (
                      <label className="block text-xs text-gray-700 dark:text-gray-300">
                        Remarks (required for Done / Reject)
                        <textarea
                          rows={2}
                          maxLength={250}
                          value={comments}
                          onChange={(e) => setComments(e.target.value)}
                          className={inputClass}
                          placeholder="Enter remarks…"
                        />
                      </label>
                    ) : null}
                  </div>
                )}
              </>
            )}
          </div>

          <div
            className={`flex flex-wrap justify-end gap-2 border-t px-4 py-3 ${
              darkMode ? "border-[#3C3C4C]" : "border-gray-200"
            }`}
          >
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 dark:border-[#3C3C4C] dark:text-gray-200"
            >
              Close
            </button>
            {isChecklistRow && canActOnChecklist && (
              <>
                {canReject ? (
                  <button
                    type="button"
                    disabled={acting}
                    onClick={handleRejectChecklist}
                    className="rounded-md border border-rose-300 bg-rose-50 px-3 py-1.5 text-xs font-medium text-rose-800 disabled:opacity-50"
                  >
                    Reject
                  </button>
                ) : null}
                <button
                  type="button"
                  disabled={acting || !canMarkDone}
                  title={!canMarkDone ? "Profile to Complete" : undefined}
                  onClick={handleCompleteChecklist}
                  className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Done
                </button>
              </>
            )}
            {!isChecklistRow && rawStatus === "draft" && isAdmin && (
              <button
                type="button"
                disabled={acting}
                onClick={() => promptForEmpCodeIfNeeded("submit")}
                className="rounded-md bg-redhalo-red px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
              >
                Submit & create employee
              </button>
            )}
            {!isChecklistRow && isCompleted && (
              <span className="self-center text-xs font-medium text-emerald-700 dark:text-emerald-400">
                Employee created — request completed
              </span>
            )}
            {!isChecklistRow && rawStatus === "pending_approval" && isAdmin && (
              <>
                <button
                  type="button"
                  disabled={acting}
                  onClick={handleReject}
                  className="rounded-md border border-rose-300 bg-rose-50 px-3 py-1.5 text-xs font-medium text-rose-800 disabled:opacity-50"
                >
                  Reject
                </button>
                <button
                  type="button"
                  disabled={acting || !canApproveRequest}
                  title={!canApproveRequest ? "Profile to Complete" : undefined}
                  onClick={() => promptForEmpCodeIfNeeded("approve")}
                  className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Approve
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <EmployeeCodePromptDialog
        open={showEmpCodeDialog}
        darkMode={darkMode}
        acting={acting}
        onCancel={closeEmpCodeDialog}
        onConfirm={confirmEmpCodeAndCreate}
      />
    </>
  );
}
